import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import StreamingStatus from './StreamingStatus';
import { 
  problemApi, 
  rcaApi, 
  PROBLEM_PRIORITIES, 
  PROBLEM_STATUSES, 
  ROOT_CAUSE_CATEGORIES,
  STATUS_LABELS,
  ROOT_CAUSE_LABELS
} from '@/api/problemApi';
import { CATEGORY_LABELS } from '@/api/incidentApi';
import { 
  AlertCircle, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  Users,
  BarChart3,
  Search,
  Brain,
  Zap,
  FileSearch,
  Activity,
  Target
} from 'lucide-react';

const ProblemManagement = () => {
  const [problems, setProblems] = useState([]);
  const [rcaAgents, setRCAAgents] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [showProblemDetail, setShowProblemDetail] = useState(false);
  const [showRCAAnalysis, setShowRCAAnalysis] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: ''
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [problemsData, rcaAgentsData, statsData] = await Promise.all([
        problemApi.getProblems(filters),
        rcaApi.getRCAAgents(),
        problemApi.getProblemStats()
      ]);
      
      setProblems(problemsData);
      setRCAAgents(rcaAgentsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching problem data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMockData = async () => {
    try {
      await Promise.all([
        problemApi.createMockProblems(),
        rcaApi.createMockRCAAgents()
      ]);
      fetchData();
    } catch (error) {
      console.error('Error creating mock data:', error);
    }
  };

  const handlePerformRCA = async (problemId, agentId) => {
    try {
      await rcaApi.performRCAAnalysis(problemId, agentId);
      fetchData(); // Refresh data after analysis
    } catch (error) {
      console.error('Error performing RCA:', error);
    }
  };

  const handleAutoDetectProblems = async (agentId) => {
    try {
      const result = await rcaApi.autoDetectProblems(agentId);
      console.log('Auto-detection result:', result);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error in auto-detection:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'NEW':
        return 'bg-blue-100 text-blue-800';
      case 'ANALYSIS_IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      case 'ROOT_CAUSE_IDENTIFIED':
        return 'bg-purple-100 text-purple-800';
      case 'SOLUTION_PROPOSED':
        return 'bg-orange-100 text-orange-800';
      case 'SOLUTION_IMPLEMENTED':
        return 'bg-green-100 text-green-800';
      case 'CLOSED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-800';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'CRITICAL':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'HIGH':
        return <TrendingUp className="w-4 h-4 text-orange-600" />;
      case 'MEDIUM':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'LOW':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Brain className="w-12 h-12 animate-pulse text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Problem Management</h2>
          <p className="text-gray-600 mt-1">
            Root Cause Analysis and Pattern Detection for Systematic Issues
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleCreateMockData} variant="outline">
            <Zap className="w-4 h-4 mr-2" />
            Create Mock Data
          </Button>
        </div>
      </div>

      {/* Streaming Status */}
      <StreamingStatus 
        title="RCA Agent Activity"
        filters={{
          agent_types: ['rca_agent', 'ai_agent'],
          log_levels: ['info', 'warning', 'error', 'success']
        }}
        compact={true}
        showLogs={true}
        maxMessages={15}
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <FileSearch className="w-4 h-4" />
              Total Problems
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.total_problems || 0}</div>
            <p className="text-xs text-gray-500 mt-1">
              {stats.problems_by_status?.NEW || 0} new
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Active Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {stats.problems_by_status?.ANALYSIS_IN_PROGRESS || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">In progress</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Root Causes Found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {stats.problems_by_status?.ROOT_CAUSE_IDENTIFIED || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">Identified</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Resolution Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {stats.resolution_metrics?.resolution_rate || 0}%
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Avg {stats.resolution_metrics?.avg_resolution_time_hours || 0}h resolution time
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filters:</span>
        </div>
        
        <select
          value={filters.status}
          onChange={(e) => setFilters({...filters, status: e.target.value})}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        <select
          value={filters.priority}
          onChange={(e) => setFilters({...filters, priority: e.target.value})}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="">All Priorities</option>
          {Object.values(PROBLEM_PRIORITIES).map((priority) => (
            <option key={priority} value={priority}>{priority}</option>
          ))}
        </select>

        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setFilters({ status: '', priority: '' })}
        >
          Clear Filters
        </Button>
      </div>

      {/* Problems List */}
      <Card>
        <CardHeader>
          <CardTitle>Problems & Root Cause Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          {problems.length === 0 ? (
            <div className="text-center py-12">
              <FileSearch className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-sm font-medium text-gray-900 mb-2">No problems detected</h3>
              <p className="text-sm text-gray-500 mb-4">
                Create mock data or wait for automatic problem detection from incident patterns.
              </p>
              <Button onClick={handleCreateMockData}>
                <Zap className="w-4 h-4 mr-2" />
                Create Sample Problems
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {problems.map((problem) => (
                <div 
                  key={problem.id} 
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    setSelectedProblem(problem);
                    setShowProblemDetail(true);
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getPriorityIcon(problem.priority)}
                        <h3 className="font-medium text-gray-900">{problem.title}</h3>
                        <Badge className={getStatusColor(problem.status)}>
                          {STATUS_LABELS[problem.status] || problem.status}
                        </Badge>
                        <Badge className={getPriorityColor(problem.priority)}>
                          {problem.priority}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-3">{problem.description}</p>
                      
                      <div className="flex items-center gap-6 text-xs text-gray-500">
                        <span>ID: {problem.problem_id}</span>
                        <span>Category: {CATEGORY_LABELS[problem.category] || problem.category}</span>
                        <span>Related Incidents: {problem.incident_count}</span>
                        <span>Affected Users: {problem.affected_users?.toLocaleString() || 0}</span>
                        {problem.downtime_minutes > 0 && (
                          <span>Downtime: {problem.downtime_minutes}min</span>
                        )}
                      </div>

                      {problem.root_cause_category && (
                        <div className="mt-2">
                          <Badge variant="outline" className="text-xs">
                            Root Cause: {ROOT_CAUSE_LABELS[problem.root_cause_category] || problem.root_cause_category}
                          </Badge>
                        </div>
                      )}

                      {problem.tags && problem.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {problem.tags.map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="ml-4 flex flex-col gap-2">
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProblem(problem);
                          setShowProblemDetail(true);
                        }}
                      >
                        View Details
                      </Button>
                      
                      {!problem.assigned_rca_agent && rcaAgents.length > 0 && (
                        <Button 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Use first appropriate RCA agent
                            const suitableAgent = rcaAgents.find(agent => 
                              agent.analysis_categories.includes(problem.category)
                            ) || rcaAgents[0];
                            handlePerformRCA(problem.id, suitableAgent.id);
                          }}
                        >
                          <Brain className="w-4 h-4 mr-1" />
                          Start RCA
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* RCA Agents Section */}
      <Card>
        <CardHeader>
          <CardTitle>RCA Agents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rcaAgents.map((agent) => (
              <div key={agent.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Brain className="w-5 h-5 text-blue-600" />
                  <h4 className="font-medium text-gray-900">{agent.name}</h4>
                </div>
                
                <p className="text-sm text-gray-600 mb-3">{agent.description}</p>
                
                <div className="space-y-2 text-xs">
                  <div>
                    <span className="font-medium">Categories:</span> {agent.analysis_categories.join(', ')}
                  </div>
                  <div>
                    <span className="font-medium">Model:</span> {agent.model_provider}/{agent.model_name}
                  </div>
                  <div>
                    <span className="font-medium">Timeframe:</span> {agent.analysis_timeframe_days} days
                  </div>
                </div>

                <div className="mt-3 flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleAutoDetectProblems(agent.id)}
                  >
                    <Search className="w-4 h-4 mr-1" />
                    Detect Problems
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Problem Detail Modal */}
      {showProblemDetail && selectedProblem && (
        <ProblemDetailModal
          problem={selectedProblem}
          rcaAgents={rcaAgents}
          onClose={() => setShowProblemDetail(false)}
          onPerformRCA={handlePerformRCA}
        />
      )}
    </div>
  );
};

// Problem Detail Modal Component
const ProblemDetailModal = ({ problem, rcaAgents, onClose, onPerformRCA }) => {
  const [problemDetails, setProblemDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProblemDetails();
  }, [problem.id]);

  const fetchProblemDetails = async () => {
    try {
      const details = await problemApi.getProblem(problem.id);
      setProblemDetails(details);
    } catch (error) {
      console.error('Error fetching problem details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
          <div className="flex items-center justify-center h-32">
            <Brain className="w-8 h-8 animate-pulse text-blue-500" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-6xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {problemDetails?.problem_id}: {problemDetails?.title}
              </h3>
              <div className="flex gap-2 mt-2">
                <Badge className={getPriorityColor(problemDetails?.priority)}>
                  {problemDetails?.priority}
                </Badge>
                <Badge className={getStatusColor(problemDetails?.status)}>
                  {STATUS_LABELS[problemDetails?.status] || problemDetails?.status}
                </Badge>
                {problemDetails?.root_cause_category && (
                  <Badge variant="outline">
                    {ROOT_CAUSE_LABELS[problemDetails.root_cause_category]}
                  </Badge>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {/* Problem Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Left Column */}
            <div className="space-y-4">
              
              {/* Description */}
              <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Description</h4>
                <p className="text-sm text-gray-700">{problemDetails?.description}</p>
              </div>

              {/* Impact Analysis */}
              {problemDetails?.impact_analysis && (
                <div className="bg-red-50 p-4 rounded-md">
                  <h4 className="text-sm font-medium text-red-900 mb-2">Impact Analysis</h4>
                  <p className="text-sm text-red-700">{problemDetails.impact_analysis}</p>
                </div>
              )}

              {/* Business Impact */}
              {problemDetails?.business_impact && (
                <div className="bg-orange-50 p-4 rounded-md">
                  <h4 className="text-sm font-medium text-orange-900 mb-2">Business Impact</h4>
                  <p className="text-sm text-orange-700">{problemDetails.business_impact}</p>
                </div>
              )}

              {/* Root Cause Summary */}
              {problemDetails?.root_cause_summary && (
                <div className="bg-purple-50 p-4 rounded-md">
                  <h4 className="text-sm font-medium text-purple-900 mb-2">Root Cause Summary</h4>
                  <p className="text-sm text-purple-700">{problemDetails.root_cause_summary}</p>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              
              {/* Metrics */}
              <div className="bg-blue-50 p-4 rounded-md">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Problem Metrics</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700 font-medium">Related Incidents:</span>
                    <div className="text-blue-900">{problemDetails?.incident_count || 0}</div>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Affected Users:</span>
                    <div className="text-blue-900">{problemDetails?.affected_users?.toLocaleString() || 0}</div>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Total Downtime:</span>
                    <div className="text-blue-900">{problemDetails?.downtime_minutes || 0} minutes</div>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Category:</span>
                    <div className="text-blue-900">{CATEGORY_LABELS[problemDetails?.category] || problemDetails?.category}</div>
                  </div>
                </div>
              </div>

              {/* Related Incidents */}
              {problemDetails?.related_incidents_details && problemDetails.related_incidents_details.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Related Incidents ({problemDetails.related_incidents_details.length})
                  </h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {problemDetails.related_incidents_details.map((incident, index) => (
                      <div key={incident.id} className="border border-gray-200 rounded p-2 text-sm">
                        <div className="font-medium">{incident.title}</div>
                        <div className="text-gray-600">
                          {incident.snow_incident_id} • {incident.priority} • {new Date(incident.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Preventive Measures */}
          {problemDetails?.preventive_measures && problemDetails.preventive_measures.length > 0 && (
            <div className="mt-6 bg-green-50 p-4 rounded-md">
              <h4 className="text-sm font-medium text-green-900 mb-3">Preventive Measures</h4>
              <ul className="space-y-2">
                {problemDetails.preventive_measures.map((measure, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-green-700">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    {measure}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* RCA Analysis Results */}
          {problemDetails?.rca_analysis && (
            <RCAAnalysisDisplay analysis={problemDetails.rca_analysis} />
          )}

          {/* Actions */}
          <div className="flex justify-between mt-6 pt-4 border-t">
            <div className="flex gap-2">
              {!problemDetails?.assigned_rca_agent && rcaAgents.length > 0 && (
                <select
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  onChange={(e) => {
                    if (e.target.value) {
                      onPerformRCA(problemDetails.id, e.target.value);
                    }
                  }}
                  defaultValue=""
                >
                  <option value="">Select RCA Agent</option>
                  {rcaAgents
                    .filter(agent => agent.analysis_categories.includes(problemDetails.category))
                    .map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// RCA Analysis Display Component
const RCAAnalysisDisplay = ({ analysis }) => {
  return (
    <div className="mt-6 bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-lg">
      <h4 className="text-lg font-medium text-purple-900 mb-4 flex items-center gap-2">
        <Brain className="w-5 h-5" />
        RCA Analysis Results
      </h4>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Left: Patterns and Timeline */}
        <div className="space-y-4">
          
          {/* Incident Patterns */}
          {analysis.incident_patterns && analysis.incident_patterns.length > 0 && (
            <div>
              <h5 className="font-medium text-purple-800 mb-2">Identified Patterns</h5>
              <div className="space-y-2">
                {analysis.incident_patterns.map((pattern, index) => (
                  <div key={index} className="bg-white p-3 rounded border border-purple-200">
                    <div className="font-medium text-sm">{pattern.pattern_type?.replace('_', ' ').toUpperCase()}</div>
                    <div className="text-sm text-gray-700 mt-1">{pattern.description}</div>
                    <div className="text-xs text-purple-600 mt-2">
                      Confidence: {Math.round(pattern.confidence * 100)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timeline Analysis */}
          {analysis.timeline_analysis && (
            <div>
              <h5 className="font-medium text-purple-800 mb-2">Timeline Analysis</h5>
              <div className="bg-white p-3 rounded border border-purple-200 text-sm">
                <div className="text-gray-700">{analysis.timeline_analysis.progression_summary}</div>
                {analysis.timeline_analysis.trigger_events && (
                  <div className="mt-2">
                    <span className="font-medium">Trigger Events:</span>
                    <ul className="list-disc list-inside mt-1">
                      {analysis.timeline_analysis.trigger_events.map((event, index) => (
                        <li key={index} className="text-gray-600">{event}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: Root Cause and Actions */}
        <div className="space-y-4">
          
          {/* Root Cause Hypothesis */}
          {analysis.root_cause_hypothesis && analysis.root_cause_hypothesis.length > 0 && (
            <div>
              <h5 className="font-medium text-purple-800 mb-2">Root Cause Analysis</h5>
              <div className="space-y-2">
                {analysis.root_cause_hypothesis.map((hypothesis, index) => (
                  <div key={index} className="bg-white p-3 rounded border border-purple-200">
                    <div className="font-medium text-sm">{hypothesis.summary}</div>
                    <div className="text-sm text-gray-700 mt-1">{hypothesis.detailed_explanation}</div>
                    <div className="text-xs text-purple-600 mt-2">
                      Confidence: {Math.round(hypothesis.confidence * 100)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preventive Actions */}
          {analysis.preventive_actions && analysis.preventive_actions.length > 0 && (
            <div>
              <h5 className="font-medium text-purple-800 mb-2">Recommended Actions</h5>
              <div className="space-y-2">
                {analysis.preventive_actions.map((action, index) => (
                  <div key={index} className="bg-white p-3 rounded border border-purple-200">
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={action.priority === 'high' ? 'destructive' : 'secondary'}
                        className="text-xs"
                      >
                        {action.type?.replace('_', ' ')}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {action.priority}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-700 mt-1">{action.description}</div>
                    {action.effort_estimate && (
                      <div className="text-xs text-gray-600 mt-1">
                        Effort: {action.effort_estimate}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function outside component
const getPriorityColor = (priority) => {
  switch (priority) {
    case 'CRITICAL':
      return 'bg-red-100 text-red-800';
    case 'HIGH':
      return 'bg-orange-100 text-orange-800';
    case 'MEDIUM':
      return 'bg-yellow-100 text-yellow-800';
    case 'LOW':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'NEW':
      return 'bg-blue-100 text-blue-800';
    case 'ANALYSIS_IN_PROGRESS':
      return 'bg-yellow-100 text-yellow-800';
    case 'ROOT_CAUSE_IDENTIFIED':
      return 'bg-purple-100 text-purple-800';
    case 'SOLUTION_PROPOSED':
      return 'bg-orange-100 text-orange-800';
    case 'SOLUTION_IMPLEMENTED':
      return 'bg-green-100 text-green-800';
    case 'CLOSED':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

export default ProblemManagement;